import React from "react";
import {
  MdAutoAwesome,
  MdBolt,
  MdChatBubbleOutline,
  MdCheck,
  MdCheckCircle,
  MdFavoriteBorder,
  MdInfoOutline,
  MdMenuBook,
  MdPlayCircleOutline,
  MdTrendingUp,
} from "react-icons/md";
import Button from "components/button";
import Card from "components/card";
import { InputField } from "components/fields";
import { OUTCOMES, buildPersonalizedContent } from "../spiritualContent";
import IconTile from "./IconTile";

const RING_RADIUS = 56;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const SectionCard = ({ icon, tone, title, children }) => (
  <Card padding="md">
    <div className="mb-4 flex items-center gap-3">
      <IconTile icon={icon} tone={tone} />
      <h2 className="text-heading">{title}</h2>
    </div>
    {children}
  </Card>
);

const NumberedList = ({ items }) => (
  <ol className="flex flex-col gap-3">
    {items.map((item, index) => (
      <li key={item} className="flex items-start gap-3 text-sm text-fg">
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-fg"
        >
          {index + 1}
        </span>
        {item}
      </li>
    ))}
  </ol>
);

const ResultsHeader = ({ visitorName, subtitle }) => (
  <div className="py-4 text-center">
    <IconTile icon={MdCheckCircle} tone="success" size="xl" className="mx-auto" />
    <h1 className="mt-4 text-title">{visitorName ? `Thank You, ${visitorName}` : "Thank You"}</h1>
    <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>
  </div>
);

const CloseNote = () => (
  <p className="text-center text-xs text-fg-muted">You can safely close this page.</p>
);

const ScoreCard = ({ result }) => (
  <Card padding="md">
    <h2 className="text-center text-heading">Your Spiritual Health Score</h2>
    <div className="my-6 flex justify-center">
      <div className="relative inline-flex items-center justify-center">
        <svg className="h-32 w-32 -rotate-90" viewBox="0 0 128 128" aria-hidden="true">
          <circle cx="64" cy="64" r={RING_RADIUS} strokeWidth="12" fill="none" stroke="currentColor" className="text-line" />
          <circle
            cx="64"
            cy="64"
            r={RING_RADIUS}
            strokeWidth="12"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeDasharray={`${(result.overallScore / 5) * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`}
            className="text-accent"
          />
        </svg>
        <p className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-fg">{result.overallScore.toFixed(1)}</span>
          <span className="text-xs text-fg-muted">out of 5.0</span>
        </p>
      </div>
    </div>

    {result.scores && Object.keys(result.scores).length > 0 && (
      <ul className="flex flex-col gap-3">
        {Object.entries(result.scores).map(([category, score]) => (
          <li key={category}>
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="capitalize text-fg-muted">{category.replace(/_/g, " ")}</span>
              <span className="font-semibold text-fg">{score.toFixed(1)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-line" aria-hidden="true">
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-500"
                style={{ width: `${(score / 5) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const DevotionalSignup = ({ visitorName, data, onChange, submitted, onSubmit }) => (
  <Card padding="md">
    <div className="mb-4 text-center">
      <IconTile icon={MdTrendingUp} size="lg" className="mx-auto" />
      <h2 className="mt-3 text-heading">Your 30-Day Growth Journey</h2>
      <p className="mt-1 text-sm text-fg-muted">
        We've created a personalized 30-day plan based on your responses. Each day builds on the
        last, helping you strengthen your faith and grow in the areas that matter most to you.
      </p>
    </div>

    {submitted ? (
      <div className="py-2 text-center" role="status">
        <IconTile icon={MdCheck} tone="success" size="lg" className="mx-auto" />
        <h3 className="mt-3 text-heading">You're All Set</h3>
        <p className="mt-1 text-sm text-fg-muted">
          Check your inbox for Day 1 of your personalized growth plan.
        </p>
        <p className="mt-3 rounded-control bg-surface-raised p-3 text-xs text-fg">
          <strong>What's ahead:</strong> 30 days of Scripture, reflection, and practical steps. At
          the end, we'll help you take the next step in your faith journey.
        </p>
      </div>
    ) : (
      <div className="flex flex-col gap-3">
        {visitorName && (
          <p className="text-center text-xs italic text-fg-muted">
            Your name has been pre-filled from your survey.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <InputField
            label="First Name"
            required
            autoComplete="given-name"
            value={data.firstName}
            onChange={(e) => onChange("firstName", e.target.value)}
          />
          <InputField
            label="Last Name"
            autoComplete="family-name"
            value={data.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
          />
        </div>
        <InputField
          label="Email"
          type="email"
          required
          autoComplete="email"
          placeholder="your@email.com"
          value={data.email}
          onChange={(e) => onChange("email", e.target.value)}
        />
        <Button
          fullWidth
          leftIcon={<MdPlayCircleOutline aria-hidden="true" className="h-5 w-5" />}
          onClick={onSubmit}
          disabled={!data.email || !data.firstName}
          className="mt-1"
        >
          Start My 30-Day Journey
        </Button>
        <p className="text-center text-xs text-fg-muted">
          Daily guidance tailored to your spiritual growth areas, with an opportunity to get
          connected at the end.
        </p>
      </div>
    )}
  </Card>
);

/**
 * Completion screen, tailored per survey type: Pathways (thank-you and prayer),
 * general (next steps and prayer) and spiritual (score, scripture, focus areas,
 * devotional signup and pastoral follow-up).
 */
const SurveyResults = ({
  variant,
  visitorName,
  result,
  prayerCard,
  devotional,
  onRequestContact,
  onSkipContact,
}) => {
  if (variant === "pathways") {
    return (
      <div className="flex flex-col gap-5">
        <ResultsHeader visitorName={visitorName} subtitle="Your responses have been received." />
        <SectionCard icon={MdCheck} tone="success" title="Response Received">
          <p className="text-sm leading-relaxed text-fg-muted">
            Thank you for sharing. Your responses help us serve you better.
          </p>
        </SectionCard>
        {prayerCard}
        <CloseNote />
      </div>
    );
  }

  if (variant === "general") {
    return (
      <div className="flex flex-col gap-5">
        <ResultsHeader
          visitorName={visitorName}
          subtitle="Your response has been submitted successfully."
        />
        <SectionCard icon={MdCheck} tone="success" title="Response Received">
          <p className="text-sm leading-relaxed text-fg-muted">
            Thank you for taking the time to complete this survey. Your feedback is valuable to us
            and helps us serve you better.
          </p>
        </SectionCard>
        <SectionCard icon={MdInfoOutline} tone="info" title="What Happens Next">
          <NumberedList
            items={[
              "Your responses will be reviewed by our team",
              "We may reach out if you provided contact information",
              "Your feedback helps us improve and serve better",
            ]}
          />
        </SectionCard>
        <SectionCard icon={MdFavoriteBorder} tone="warning" title="We Appreciate You">
          <p className="text-sm leading-relaxed text-fg-muted">
            {visitorName ? `${visitorName}, we` : "We"} truly appreciate you taking the time to share
            your thoughts with us. Your input makes a real difference.
          </p>
        </SectionCard>
        {prayerCard}
        <CloseNote />
      </div>
    );
  }

  const { scripture, focusAreas } = buildPersonalizedContent(result);

  return (
    <div className="flex flex-col gap-5">
      <ResultsHeader
        visitorName={visitorName}
        subtitle="Your spiritual health assessment is complete."
      />

      {result?.overallScore ? <ScoreCard result={result} /> : null}

      <SectionCard icon={MdMenuBook} tone="info" title="Scripture for Your Journey">
        <p className="text-sm italic leading-relaxed text-fg-muted">{scripture}</p>
      </SectionCard>

      <SectionCard icon={MdBolt} tone="brand" title="Your Key Focus Areas">
        <NumberedList items={focusAreas} />
      </SectionCard>

      <SectionCard icon={MdAutoAwesome} tone="success" title="What You Can Expect">
        <ul className="flex flex-col gap-2">
          {OUTCOMES.map((outcome) => (
            <li key={outcome} className="flex items-start gap-2 text-sm text-fg">
              <MdCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-success-fg" />
              {outcome}
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard icon={MdChatBubbleOutline} tone="warning" title="A Note From Us">
        <p className="text-sm leading-relaxed text-fg-muted">
          {visitorName ? `${visitorName}, thank` : "Thank"} you for taking the time to share where
          you are in your spiritual journey. We believe God is doing something special in your life,
          and we're honored to walk alongside you.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-fg-muted">
          No matter where your score landed, there's always room to grow closer to God. We're here
          to support you every step of the way.
        </p>
      </SectionCard>

      <DevotionalSignup visitorName={visitorName} {...devotional} />

      <Card padding="md" className="items-center text-center">
        <IconTile icon={MdFavoriteBorder} size="lg" />
        <h2 className="mt-3 text-heading">We're Here For You</h2>
        <p className="mt-1 text-sm text-fg-muted">
          Would you like someone from our pastoral team to connect with you?
        </p>
        <Button fullWidth className="mt-4" onClick={onRequestContact}>
          Yes, I'd Like to Connect
        </Button>
        <Button variant="ghost" fullWidth className="mt-2" onClick={onSkipContact}>
          No Thanks, I'm Good for Now
        </Button>
      </Card>
    </div>
  );
};

export default SurveyResults;
